import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminApi, type ArticleSource } from '@/api';

export function ArticleSourcesList() {
  const [items, setItems] = useState<ArticleSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    adminApi.articleSources.list().then(setItems).catch((e) => setError(e.message)).finally(() => setLoading(false));
  }, []);

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this article source? Articles will be deleted too.')) return;
    try {
      await adminApi.articleSources.delete(id);
      setItems((prev) => prev.filter((p) => p.id !== id));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    }
  };

  const handleMove = async (index: number, direction: 'up' | 'down') => {
    const newItems = [...items];
    const swap = direction === 'up' ? index - 1 : index + 1;
    if (swap < 0 || swap >= newItems.length) return;
    [newItems[index], newItems[swap]] = [newItems[swap]!, newItems[index]!];
    const ids = newItems.map((p) => p.id);
    try {
      await adminApi.articleSources.reorder(ids);
      setItems(newItems);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Reorder failed');
    }
  };

  if (loading) return <div className="text-slate-500">Loading…</div>;
  if (error) return <div className="text-red-600">{error}</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold text-slate-800">Article Sources</h1>
        <Link
          to="/syncs/sources/new"
          className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
        >
          Add source
        </Link>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Feed URL</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Articles</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase w-24">Reorder</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-slate-600 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {items.map((p, idx) => (
              <tr key={p.id}>
                <td className="px-4 py-3 text-slate-800 font-medium">{p.name}</td>
                <td className="px-4 py-3 text-slate-600 text-sm max-w-xs truncate">{p.feedUrl ?? '-'}</td>
                <td className="px-4 py-3 text-slate-600">{p._count?.articles ?? 0}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${p.isActive ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-600'}`}>
                    {p.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={() => handleMove(idx, 'up')}
                      disabled={idx === 0}
                      className="rounded border border-slate-300 px-2 py-0.5 text-xs disabled:opacity-40 hover:bg-slate-50"
                      title="Move up"
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      onClick={() => handleMove(idx, 'down')}
                      disabled={idx === items.length - 1}
                      className="rounded border border-slate-300 px-2 py-0.5 text-xs disabled:opacity-40 hover:bg-slate-50"
                      title="Move down"
                    >
                      ↓
                    </button>
                  </div>
                </td>
                <td className="px-4 py-3 text-right">
                  <Link
                    to={`/syncs/sources/${p.id}/edit`}
                    className="text-indigo-600 hover:underline text-sm font-medium mr-4"
                  >
                    Edit
                  </Link>
                  <button
                    type="button"
                    onClick={() => handleDelete(p.id)}
                    className="text-red-600 hover:underline text-sm font-medium"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
